import {
  RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
  type InvestigationActivity as RxInvestigationActivity,
} from "$global-providers/rxdb-provider/collections/investigation-activity";
import { useRxDBContext } from "$global-providers/rxdb-provider/provider";
import type { RxDocument } from "rxdb";
import { useRxData } from "rxdb-hooks";

export enum VitalInvestigationsLoincCodes {
  WEIGHT = "29463-7",
  VITALS = "85353-1",
  ALLERGY = "95763-9",
}

interface InvestigationOrderedProperty<T> {
  value: T;
  orderedProperties?: InvestigationOrderedProperty<T>[];
}

const recursiveExtractOneValue = <T>(
  orderedProperty: InvestigationOrderedProperty<T>,
  indexOverrides: number[] = null,
) => {
  if (orderedProperty.orderedProperties) {
    return recursiveExtractOneValue(
      orderedProperty.orderedProperties[indexOverrides ? indexOverrides[0] : 0],
      indexOverrides ? indexOverrides.slice(1) : null,
    );
  }
  return orderedProperty.value;
};

const extractHealthAndWeight = (
  weightInvestigation?: RxDocument<RxInvestigationActivity>,
  allergiesInvestigations?: RxDocument<RxInvestigationActivity>[],
) => ({
  weight: weightInvestigation?._data?.outcome?.orderedProperties
    ? recursiveExtractOneValue(
        weightInvestigation._data.outcome.orderedProperties.find(
          (property: {
            investigationMeta?: {
              coding?: { loinc?: { code?: string } };
            };
          }) =>
            property.investigationMeta?.coding?.loinc?.code ===
            VitalInvestigationsLoincCodes.WEIGHT,
        ) as InvestigationOrderedProperty<number>,
      )
    : null,
  allergies: allergiesInvestigations.length
    ? allergiesInvestigations.map((item) =>
        item._data.outcome
          ? recursiveExtractOneValue(
              item._data.outcome
                .orderedProperties[0] as InvestigationOrderedProperty<string>,
            )
          : null,
      )
    : [],
});

export const useWeightAndAllergies = ({
  patientId,
}: {
  patientId: string;
}): {
  loading: boolean;
  weight: number | null;
  allergies: string[];
} => {
  const {
    replicationState: {
      investigationActivity: { isPulling },
    },
  } = useRxDBContext();
  const investigationActivities = useRxData<RxInvestigationActivity>(
    RXDB_INVESTIGATION_ACTIVITY_COLLECTION_NAME,
    (collection) =>
      collection.find({
        selector: {
          patientId: {
            $eq: patientId,
          },
        },
      }),
  );

  const { weight, allergies } = extractHealthAndWeight(
    investigationActivities.result?.find(
      (investigationActivity) =>
        investigationActivity._data.catalogueItem.coding?.loinc?.code ===
          VitalInvestigationsLoincCodes.WEIGHT ||
        investigationActivity._data.catalogueItem.coding?.loinc?.code ===
          VitalInvestigationsLoincCodes.VITALS,
    ),
    investigationActivities.result?.filter(
      (investigationActivity) =>
        investigationActivity._data.catalogueItem.coding?.loinc?.code ===
        VitalInvestigationsLoincCodes.ALLERGY,
    ),
  );

  return { weight, allergies, loading: isPulling };
};
